import React from 'react';
import Modal from './Modal';

const HowToPlayModal = ({ isOpen, onClose, competitionType = 'knockout' }) => {
  return (
    <Modal title="How To Play" isOpen={isOpen} onClose={onClose}>
      <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-2 -mr-2">
        <h2 className="text-xl md:text-2xl font-bold text-brand-text mb-3">How to Play</h2>
        
        {/* Overview */}
        <div className="space-y-3">
          <p className="text-sm md:text-base text-brand-text-muted">
            FootyCoach offers exciting football prediction competitions where you can test your knowledge
            and compete with friends!
          </p>
        </div>

        {/* Competition Type Specific Content */}
        <div className="space-y-4">
          {competitionType === 'knockout' && (
            <div className="bg-ui-green bg-opacity-10 border border-[#284239] border-opacity-20 rounded-lg p-3 md:p-4">
              <h3 className="text-base md:text-lg font-bold text-action-primary mb-2 md:mb-3">🏆 Knockout Competitions</h3>
              <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-brand-text-muted">
                <div className="flex items-start">
                  <span className="text-action-primary mr-2">•</span>
                  <span>Select one team per matchday that you think will win</span>
                </div>
                <div className="flex items-start">
                  <span className="text-action-primary mr-2">•</span>
                  <span>If your team loses or draws, you're eliminated</span>
                </div>
                <div className="flex items-start">
                  <span className="text-action-primary mr-2">•</span>
                  <span>You cannot select the same team twice (where applicable)</span>
                </div>
                <div className="flex items-start">
                  <span className="text-action-primary mr-2">•</span>
                  <span>Last player(s) standing wins!</span>
                </div>
              </div>
            </div>
          )}

          {competitionType === 'season' && (
            <div className="bg-ui-green bg-opacity-10 border border-[#284239] border-opacity-20 rounded-lg p-3 md:p-4">
              <h3 className="text-base md:text-lg font-bold text-action-primary mb-2 md:mb-3">📊 Season Competitions</h3>
              <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-brand-text-muted">
                <div className="flex items-start">
                  <span className="text-action-primary mr-2">•</span>
                  <span>Make predictions for each matchday throughout the season</span>
                </div>
                <div className="flex items-start">
                  <span className="text-action-primary mr-2">•</span>
                  <span>Earn points based on prediction accuracy</span>
                </div>
                <div className="flex items-start">
                  <span className="text-action-primary mr-2">•</span>
                  <span>Player with the most points at season end wins</span>
                </div>
                <div className="flex items-start">
                  <span className="text-action-primary mr-2">•</span>
                  <span>Track your progress on the leaderboard</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Important Rules */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 md:p-4">
          <h3 className="text-base md:text-lg font-bold text-yellow-900 mb-2 md:mb-3">⚠️ Important Rules</h3>
          <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-yellow-800">
            <div className="flex items-start">
              <span className="text-yellow-600 mr-2">•</span>
              <span>All predictions must be submitted before the deadline</span>
            </div>
            <div className="flex items-start">
              <span className="text-yellow-600 mr-2">•</span>
              <span>Late submissions will not be accepted</span>
            </div>
            <div className="flex items-start">
              <span className="text-yellow-600 mr-2">•</span>
              <span>Only one account per person per competition</span>
            </div>
            <div className="flex items-start">
              <span className="text-yellow-600 mr-2">•</span>
              <span>Results based on official 90-minute match results</span>
            </div>
            <div className="flex items-start">
              <span className="text-yellow-600 mr-2">•</span>
              <span>Postponed/abandoned games: no points awarded</span>
            </div>
          </div>
        </div>

        {/* Getting Started */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
          <h3 className="text-base md:text-lg font-bold text-ui-success mb-2 md:mb-3">🚀 Getting Started</h3>
          <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-ui-success">
            <div className="flex items-start">
              <span className="flex-shrink-0 w-4 h-4 md:w-5 md:h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2 mt-0.5">1</span>
              <span>Browse available competitions</span>
            </div>
            <div className="flex items-start">
              <span className="flex-shrink-0 w-4 h-4 md:w-5 md:h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2 mt-0.5">2</span>
              <span>Join competitions that interest you</span>
            </div>
            <div className="flex items-start">
              <span className="flex-shrink-0 w-4 h-4 md:w-5 md:h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2 mt-0.5">3</span>
              <span>Make your predictions before the deadline</span>
            </div>
            <div className="flex items-start">
              <span className="flex-shrink-0 w-4 h-4 md:w-5 md:h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2 mt-0.5">4</span>
              <span>Track results and your ranking</span>
            </div>
            <div className="flex items-start">
              <span className="flex-shrink-0 w-4 h-4 md:w-5 md:h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2 mt-0.5">5</span>
              <span>Win bragging rights!</span>
            </div>
          </div>
        </div>

        <div className="text-center pt-2">
          <p className="text-xs md:text-sm text-brand-text-muted">
            Ready to test your football knowledge? Good luck!
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default HowToPlayModal;
